import React, { useEffect, useRef, useState } from 'react';
import { Card, Loading, Tag, Button, Toast, ProgressBar, Popup } from 'antd-mobile';
import {
  RightOutline,
  CheckOutline,
  CalendarOutline,
  TextOutline,
  FileOutline,
  UserOutline,
  UnorderedListOutline,
  EnvironmentOutline,
  CloseCircleOutline,
  AddOutline,
} from 'antd-mobile-icons';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { mobileApi } from '../api';
import TestTaskDispatch from './TestTaskDispatch';
import {
  T,
  cardStyle,
  statusTag,
  SectionCard,
  CellField,
  CellInput,
  cellDateStyle,
} from '../theme';

function TestTaskPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [legalEntity, setLegalEntity] = useState('');
  const [samplingDate, setSamplingDate] = useState('');
  const [expertTaskNo, setExpertTaskNo] = useState('');
  const dirtyRef = useRef(false);
  const changeRef = useRef(0);
  const [cancelIdx, setCancelIdx] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const { data, isLoading } = useQuery(
    ['goszadanieGoTask', taskId],
    () => mobileApi.goszadanieGoTask(taskId),
    {
      onError: (err) => {
        const detail = err?.response?.data?.detail || 'Не удалось загрузить задание';
        Toast.show({ icon: 'fail', content: String(detail) });
      },
    }
  );

  const { data: reasonsData } = useQuery(
    'goszadanieGoCancelReasons',
    () => mobileApi.goszadanieGoCancelReasons()
  );
  const { data: workCodesData } = useQuery(
    'goszadanieGoWorkCodes',
    () => mobileApi.goszadanieGoWorkCodes()
  );
  const cancelReasons = reasonsData?.data?.items || [];
  const workCodeOptions = workCodesData?.data?.items || [];

  const task = data?.data;

  const reloadTask = () => {
    queryClient.invalidateQueries(['goszadanieGoTask', taskId]);
    queryClient.invalidateQueries('goszadanieGoTasks');
  };

  const cancelCode = async (reason) => {
    if (cancelIdx === null) return;
    setActionLoading(true);
    try {
      await mobileApi.goszadanieGoCancelCode(taskId, cancelIdx, reason);
      Toast.show({ icon: 'success', content: 'Код работы отменён' });
      setCancelIdx(null);
      reloadTask();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Не удалось отменить код';
      Toast.show({ icon: 'fail', content: String(detail) });
    } finally {
      setActionLoading(false);
    }
  };

  const addCode = async (code) => {
    setActionLoading(true);
    try {
      await mobileApi.goszadanieGoAddCode(taskId, code);
      Toast.show({ icon: 'success', content: `Код ${code} добавлен` });
      setAddOpen(false);
      reloadTask();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Не удалось добавить код';
      Toast.show({ icon: 'fail', content: String(detail) });
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (task) {
      setLegalEntity(task.legal_entity || '');
      setSamplingDate(task.sampling_date || '');
      setExpertTaskNo(task.expert_task_no || '');
    }
  }, [task]);

  const buildGeneral = (overrides = {}) => ({
    legal_entity: (overrides.legalEntity ?? legalEntity).trim() || '',
    sampling_date: (overrides.samplingDate ?? samplingDate) || null,
    expert_task_no: (overrides.expertTaskNo ?? expertTaskNo).trim() || '',
  });

  const saveGeneral = async (overrides) => {
    try {
      await mobileApi.goszadanieGoSaveGeneral(taskId, buildGeneral(overrides));
      Toast.show({ icon: 'success', content: 'Сохранено' });
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Ошибка сохранения';
      Toast.show({ icon: 'fail', content: String(detail) });
      return false;
    }
  };

  const flushSave = () => {
    if (!dirtyRef.current) return;
    const at = changeRef.current;
    saveGeneral().then((ok) => {
      if (ok && changeRef.current === at) dirtyRef.current = false;
    });
  };

  const changeLegalEntity = (v) => {
    dirtyRef.current = true;
    changeRef.current += 1;
    setLegalEntity(v);
  };
  const changeSamplingDate = (v) => {
    dirtyRef.current = true;
    changeRef.current += 1;
    setSamplingDate(v);
    const at = changeRef.current;
    saveGeneral({ samplingDate: v }).then((ok) => {
      if (ok && changeRef.current === at) dirtyRef.current = false;
    });
  };
  const changeExpertTaskNo = (v) => {
    dirtyRef.current = true;
    changeRef.current += 1;
    setExpertTaskNo(v);
  };

  const blurInput = (e) => {
    if (e?.target?.blur) e.target.blur();
  };

  if (isLoading || !task) {
    return <Loading style={{ display: 'block', margin: '20px auto' }} />;
  }

  const codes = task.work_codes || [];
  const doneCount = codes.filter((c) => c.done).length;
  const cancelledCount = codes.filter((c) => c.cancelled).length;
  const allDone = codes.length > 0 && doneCount + cancelledCount === codes.length;
  const taskActive = task.status === 'active';

  return (
    <div>
      <Button size="mini" fill="none" onClick={() => navigate('/test')} style={{ marginBottom: 8 }}>
        ← К списку заданий
      </Button>

      <Card style={cardStyle}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}
        >
          <div
            style={{
              display: 'flex',
              gap: 8,
              flex: 1,
              fontSize: 16,
              fontWeight: 700,
              color: T.text,
              lineHeight: 1.35,
            }}
          >
            <EnvironmentOutline style={{ fontSize: 18, color: T.primary, flexShrink: 0, marginTop: 1 }} />
            {task.address || 'Адрес не указан'}
          </div>
          {statusTag(task.status)}
        </div>
        {task.task_date && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 10,
              fontSize: 13,
              color: T.label,
            }}
          >
            <CalendarOutline style={{ fontSize: 14 }} />
            Дата задания: {dayjs(task.task_date).format('DD.MM.YYYY')}
          </div>
        )}
        {codes.length > 0 && (
          <div
            style={{
              marginTop: 12,
              background: T.subBlockBg,
              borderRadius: 10,
              padding: '10px 12px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 6,
              }}
            >
              <span style={{ fontSize: 12, color: T.label }}>
                Выполнено {doneCount} из {codes.length}
                {cancelledCount > 0 && ` · отменено: ${cancelledCount}`}
              </span>
              {allDone && (
                <span
                  style={{
                    fontSize: 12,
                    color: T.success,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <CheckOutline /> Все коды закрыты
                </span>
              )}
            </div>
            <ProgressBar
              percent={Math.round(((doneCount + cancelledCount) / codes.length) * 100)}
              style={{
                '--track-width': '4px',
                '--fill-color': allDone ? T.success : T.primary,
              }}
            />
          </div>
        )}
      </Card>

      <TestTaskDispatch taskId={task.id} />

      <SectionCard title="Общие данные" icon={<FileOutline />}>
        <CellField
          label="Экспертное задание №"
          hint="Подтягивается из плана выезда, можно поправить вручную"
        >
          <CellInput
            value={expertTaskNo}
            onChange={changeExpertTaskNo}
            onBlur={flushSave}
            onEnterPress={blurInput}
            enterKeyHint="done"
            placeholder="ЭЗ-(1)-РСН-…"
            clearable
          />
        </CellField>

        <CellField
          label="ЮЛ/ИП (проверяемое лицо)"
          icon={<UserOutline />}
          hint="Юридическое лицо/индивидуальный предприниматель, в отношении которого проводится проверка"
        >
          <CellInput
            value={legalEntity}
            onChange={changeLegalEntity}
            onBlur={flushSave}
            onEnterPress={blurInput}
            enterKeyHint="done"
            placeholder="Укажите ЮЛ/ИП"
            clearable
          />
        </CellField>

        <CellField label="Дата отбора проб/проведения измерений" icon={<CalendarOutline />} style={{ marginBottom: 0 }}>
          <input
            type="date"
            value={samplingDate}
            onChange={(e) => changeSamplingDate(e.target.value)}
            style={cellDateStyle}
          />
        </CellField>
      </SectionCard>

      <SectionCard
        title="Коды работ"
        icon={<UnorderedListOutline />}
        extra={
          codes.length > 0 && (
            <span style={{ fontSize: 13, color: T.label }}>
              {doneCount}/{codes.length}
            </span>
          )
        }
      >
        {codes.map((c, idx) => (
          <div
            key={idx}
            onClick={() => {
              if (!c.cancelled) navigate(`/test/${task.id}/code/${idx}`);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minHeight: 52,
              padding: '10px 12px',
              marginBottom: idx < codes.length - 1 ? 8 : 0,
              background: T.subBlockBg,
              borderRadius: 10,
              borderLeft: `3px solid ${c.cancelled ? '#c7c7cc' : c.done ? T.success : T.primary}`,
              cursor: c.cancelled ? 'default' : 'pointer',
              opacity: c.cancelled ? 0.75 : 1,
            }}
          >
            <Tag
              color={c.cancelled ? 'danger' : c.done ? 'success' : 'primary'}
              style={{ fontSize: 13, padding: '4px 10px', borderRadius: 8 }}
            >
              {c.code}
            </Tag>
            <div
              style={{
                flex: 1,
                fontSize: 13,
                color: c.cancelled ? T.danger : c.done ? T.success : T.label,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              {c.done && !c.cancelled && <CheckOutline />}
              {c.cancelled ? (
                <span>
                  Отменена{c.cancelled_by ? ` · ${c.cancelled_by}` : ''}
                  <div style={{ fontSize: 11, color: T.label, marginTop: 2 }}>{c.cancel_reason}</div>
                </span>
              ) : c.done ? (
                `Выполнено${c.done_by ? ` · ${c.done_by}` : ''}`
              ) : (
                'Открыть чек-лист'
              )}
            </div>
            {taskActive && !c.cancelled && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setCancelIdx(idx);
                }}
                title="Отменить код работы"
                style={{ display: 'flex', alignItems: 'center', padding: 4 }}
              >
                <CloseCircleOutline style={{ fontSize: 20, color: '#ff4d4f' }} />
              </span>
            )}
            {!c.cancelled && <RightOutline style={{ color: '#c7c7cc' }} />}
          </div>
        ))}

        {taskActive && (
          <Button
            block
            fill="outline"
            color="primary"
            size="small"
            style={{ marginTop: codes.length > 0 ? 10 : 0 }}
            onClick={() => setAddOpen(true)}
          >
            <AddOutline style={{ marginRight: 4 }} />
            Добавить код работ
          </Button>
        )}
      </SectionCard>

      {task.comment && (
        <Card style={{ ...cardStyle, marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 6, fontSize: 13, color: T.label }}>
            <TextOutline style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }} />
            <span>{task.comment}</span>
          </div>
        </Card>
      )}

      <Popup
        visible={cancelIdx !== null}
        onMaskClick={() => !actionLoading && setCancelIdx(null)}
        bodyStyle={{ borderRadius: '12px 12px 0 0', maxHeight: '60vh', overflowY: 'auto' }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #eee',
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Причина отмены кода {cancelIdx !== null ? codes[cancelIdx]?.code : ''}</span>
          <span
            onClick={() => !actionLoading && setCancelIdx(null)}
            style={{ color: T.primary, fontSize: 14, fontWeight: 400 }}
          >
            Закрыть
          </span>
        </div>
        <div style={{ padding: '4px 16px 16px' }}>
          {cancelReasons.map((r) => (
            <div
              key={r}
              onClick={() => !actionLoading && cancelCode(r)}
              style={{
                padding: '12px 0',
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
                fontSize: 13,
                color: T.text,
              }}
            >
              {r}
            </div>
          ))}
        </div>
      </Popup>

      <Popup
        visible={addOpen}
        onMaskClick={() => !actionLoading && setAddOpen(false)}
        bodyStyle={{ borderRadius: '12px 12px 0 0', maxHeight: '60vh', overflowY: 'auto' }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #eee',
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Добавить код работ</span>
          <span
            onClick={() => !actionLoading && setAddOpen(false)}
            style={{ color: T.primary, fontSize: 14, fontWeight: 400 }}
          >
            Закрыть
          </span>
        </div>
        <div style={{ padding: '4px 16px 16px' }}>
          {workCodeOptions.map((wc) => (
            <div
              key={wc.code}
              onClick={() => !actionLoading && addCode(wc.code)}
              style={{
                padding: '12px 0',
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
              }}
            >
              <Tag color="primary" style={{ fontSize: 13, padding: '4px 10px', borderRadius: 8 }}>
                {wc.code}
              </Tag>
              <div style={{ fontSize: 12, color: T.label, marginTop: 4 }}>{wc.name}</div>
            </div>
          ))}
        </div>
      </Popup>
    </div>
  );
}

export default TestTaskPage;
